/**
 * Slice C.2 · EarningsAnswer.
 * ---------------------------
 * Reads canonical `presentation.data` mirrored from the domain
 * authority `domain.instructor_earnings.get_instructor_earnings`.
 *
 * Canonical fields (server → visible):
 *   • period            → "Period"       (raw string, snake→spaces)
 *   • date_from         → "From"         (ISO date)
 *   • date_to           → "To"           (ISO date)
 *   • paid_total_pence  → "Paid total"   (formatted £N.NN)
 *   • paid_count        → "Paid lessons" (integer)
 *   • currency          → "Currency"     (raw code, only if !== "GBP")
 *
 * The renderer NEVER invents a net figure, breakdown or tax split
 * — the canonical authority owns "paid" only. Display formatting
 * (pence → pounds string) does not alter the payload.
 *
 * Graceful compat (safe, C.1 legacy shape):
 *   • gross_pounds / net_pounds / breakdown are rendered ONLY when
 *     the domain authority genuinely emits them (currently it does
 *     not). No zeros or placeholders are ever fabricated.
 */
function _formatPence(pence) {
  if (!Number.isInteger(pence)) return null;
  const pounds = pence / 100;
  return `£${pounds.toFixed(2)}`;
}

function _prettyPeriod(period) {
  if (typeof period !== "string" || period.length === 0) return null;
  return period.replace(/_/g, " ");
}

export function EarningsAnswer({ presentation }) {
  const d = presentation.data || {};

  // Canonical fields (Slice C.2)
  const period          = _prettyPeriod(d.period);
  const dateFrom        = typeof d.date_from === "string" ? d.date_from : null;
  const dateTo          = typeof d.date_to   === "string" ? d.date_to   : null;
  const paidTotalString = _formatPence(d.paid_total_pence);
  const paidCount       = Number.isInteger(d.paid_count) ? d.paid_count : null;
  const currency        = typeof d.currency === "string" ? d.currency   : null;

  // Legacy-compat (only rendered if the field is genuinely present)
  const gross     = Number.isFinite(d.gross_pounds) ? d.gross_pounds : null;
  const net       = Number.isFinite(d.net_pounds)   ? d.net_pounds   : null;
  const breakdown = Array.isArray(d.breakdown) ? d.breakdown : null;

  const hasAnyCanonical =
    period !== null || paidTotalString !== null || paidCount !== null
    || dateFrom !== null || dateTo !== null;
  const hasAnyLegacy = gross !== null || net !== null;

  return (
    <div data-testid="ui-response-earnings-answer" className="space-y-3">
      <p
        className="text-[14px] leading-relaxed text-zinc-100"
        data-testid="ui-earnings-narration"
      >
        {presentation.narration}
      </p>
      {(hasAnyCanonical || hasAnyLegacy) && (
        <dl className="text-[13px] grid grid-cols-2 gap-x-3 gap-y-1 text-zinc-300">
          {period && (
            <>
              <dt className="text-zinc-500">Period</dt>
              <dd data-testid="ui-earnings-period">{period}</dd>
            </>
          )}
          {dateFrom && (
            <>
              <dt className="text-zinc-500">From</dt>
              <dd data-testid="ui-earnings-date-from">{dateFrom}</dd>
            </>
          )}
          {dateTo && (
            <>
              <dt className="text-zinc-500">To</dt>
              <dd data-testid="ui-earnings-date-to">{dateTo}</dd>
            </>
          )}
          {paidTotalString && (
            <>
              <dt className="text-zinc-500">Paid total</dt>
              <dd data-testid="ui-earnings-paid-total">{paidTotalString}</dd>
            </>
          )}
          {paidCount !== null && (
            <>
              <dt className="text-zinc-500">Paid lessons</dt>
              <dd data-testid="ui-earnings-paid-count">{paidCount}</dd>
            </>
          )}
          {currency && currency !== "GBP" && (
            <>
              <dt className="text-zinc-500">Currency</dt>
              <dd data-testid="ui-earnings-currency">{currency}</dd>
            </>
          )}
          {gross !== null && (
            <>
              <dt className="text-zinc-500">Gross</dt>
              <dd data-testid="ui-earnings-gross">£{gross.toFixed(2)}</dd>
            </>
          )}
          {net !== null && (
            <>
              <dt className="text-zinc-500">Net</dt>
              <dd data-testid="ui-earnings-net">£{net.toFixed(2)}</dd>
            </>
          )}
        </dl>
      )}
      {breakdown && breakdown.length > 0 && (
        <ul data-testid="ui-earnings-breakdown" className="text-[12.5px] text-zinc-400 space-y-0.5">
          {breakdown.slice(0, 10).map((row, i) => (
            <li key={i}>
              {typeof row?.label === "string" ? row.label : "—"}
              {Number.isFinite(row?.amount_pounds)
                ? <>: <span className="text-zinc-200">£{row.amount_pounds.toFixed(2)}</span></>
                : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default EarningsAnswer;
